import { Box, Divider, Link, Typography } from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import ArrowForward from "@mui/icons-material/ArrowForward";
import InviteeProductFaqAccordions from "./InviteeProductFaqAccordions";
import type * as React from "react";
import { useHistory } from "react-router-dom";
import GLLogo from "assets/images/GL-Logo.svg";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import GLButton from "components/v3/sharedComponents/GLButton";
import Video from "components/v3/sharedComponents/Video";
import { COLORS } from "components/v3/Theme/colors";
import {
  GENOMIC_LIFE_INVITE_RECIPIENT_VIDEO_URL,
  GENOMIC_LIFE_PUBLIC_PRIVACY_POLICY_URL,
  GENOMIC_LIFE_PUBLIC_TERMS_OF_USE_URL
} from "constants/externalPathConstants";
import { PATHS } from "constants/pathConstants";
import PageTitle from "utils/PageTitle";
import GenomicLifeLegalDisclaimer from "components/v3/sharedComponents/GenomicLifeLegalDisclaimer";
import { MEMBERSHIP_ANNUAL_PRICE_USD } from "constants/membershipPriceConstants";

function ProductPriceBlock({
  onCheckout,
  sx
}: {
  onCheckout: () => void;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      sx={[
        {
          alignItems: "stretch",
          display: "flex",
          flex: { lg: "0 1 40%", xs: "none" },
          flexDirection: "column",
          gap: 0,
          minWidth: 0
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])
      ]}
    >
      <Typography component="p" variant="body_dark_gray" sx={{ m: 0 }}>
        Your yearly cost
      </Typography>
      <Typography
        component="p"
        sx={{
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "0.02em",
          m: 0,
          mt: 0.5,
          typography: { md: "h5_accent2", xs: "h5_accent2" }
        }}
      >
        $ {MEMBERSHIP_ANNUAL_PRICE_USD.toFixed(2)}
      </Typography>
      <GLButton
        buttonType="primary"
        onClick={onCheckout}
        rightIcon={<ArrowForward />}
        text="Checkout"
        sx={{
          alignSelf: { lg: "flex-start", xs: "stretch" },
          maxWidth: 280,
          mt: 2.5
        }}
      />
    </Box>
  );
}

function ProductBulletsBlock({ sx }: { sx?: SxProps<Theme> }) {
  return (
    <Box
      sx={[
        {
          display: "flex",
          flex: { lg: "1 1 0", xs: "none" },
          flexDirection: "column",
          minWidth: 0,
          overflow: "visible",
          pt: { lg: 0.25, xs: 0 }
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])
      ]}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0,
          maxWidth: 500,
          width: "100%"
        }}
      >
        <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1.5 }}>
          <ArrowForward
            sx={{
              color: COLORS.PRIMARY,
              flexShrink: 0,
              fontSize: 18,
              mt: 0.35
            }}
          />
          <Typography component="p" variant="body_dark_gray" sx={{ m: 0 }}>
            Annual plan required.
          </Typography>
        </Box>
        <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1.5 }}>
          <ArrowForward
            sx={{
              color: COLORS.PRIMARY,
              flexShrink: 0,
              fontSize: 18,
              mt: 0.35
            }}
          />
          <Typography component="p" variant="body_dark_gray" sx={{ m: 0 }}>
            Membership automatically renews annually and is subject to our
            refund and cancellation policy
          </Typography>
        </Box>
        <Typography component="p" variant="body_dark_gray" sx={{ m: 0, mt: 1 }}>
          Please see our{" "}
          <Link
            href={GENOMIC_LIFE_PUBLIC_TERMS_OF_USE_URL}
            rel="noopener noreferrer"
            target="_blank"
            underline="always"
            sx={{
              color: COLORS.DARK_GRAY,
              textDecorationColor: COLORS.DARK_GRAY,
              typography: "body_dark_gray"
            }}
          >
            Terms of Use
          </Link>{" "}
          and{" "}
          <Link
            href={GENOMIC_LIFE_PUBLIC_PRIVACY_POLICY_URL}
            rel="noopener noreferrer"
            target="_blank"
            underline="always"
            sx={{
              color: COLORS.DARK_GRAY,
              textDecorationColor: COLORS.DARK_GRAY,
              typography: "body_dark_gray"
            }}
          >
            Privacy Policy
          </Link>{" "}
          for details.
        </Typography>
      </Box>
    </Box>
  );
}

const GenomicLifeCompleteProductPage: React.FC = () => {
  const history = useHistory();

  PageTitle("Genomic Life Complete");

  const goCheckout = () => {
    history.push(PATHS.ONBOARDING_UNPAID_INVITE_PROFILE);
  };

  return (
    <GradientFramedLayout
      makeLogoClickable={false}
      showFooter={false}
      showHeader={false}
    >
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: { md: 4, xs: 3 },
          marginX: "auto",
          maxWidth: 1120,
          minWidth: 0,
          pb: { md: 10, xs: 8 },
          pt: { md: 6, xs: 4 },
          px: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <Box
          alt="Genomic Life"
          component="img"
          src={GLLogo}
          sx={{
            alignSelf: "flex-start",
            display: "block",
            height: 44,
            width: 185
          }}
        />

        <Box
          sx={{
            bgcolor: COLORS.WHITE,
            borderRadius: "24px",
            boxShadow: "0 12px 40px rgba(17, 24, 39, 0.06)",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: { md: 5, xs: 4 },
            overflow: "visible",
            p: { md: 6, xs: 3 },
            width: "100%"
          }}
        >
          <Typography component="h1" variant="h4_black" sx={{ m: 0 }}>
            Genomic Life Complete
          </Typography>
          <Typography
            component="p"
            variant="body_lg_dark_gray"
            sx={{ m: 0, maxWidth: 880 }}
          >
            From DNA screenings to 1-on-1 support, education and navigation, we
            help members find a path forward that’s tailored to your individual
            needs.
          </Typography>

          <Box
            sx={{
              borderRadius: "16px",
              boxShadow:
                "0px 10px 15px -3px rgba(0, 0, 0, 0.08), 0px 4px 6px -2px rgba(0, 0, 0, 0.04)",
              overflow: "hidden",
              width: "100%"
            }}
          >
            <Video
              controls
              height="100%"
              muted
              playing={false}
              style={{
                aspectRatio: "16/9",
                height: "100%",
                width: "100%"
              }}
              width="100%"
              url={GENOMIC_LIFE_INVITE_RECIPIENT_VIDEO_URL}
            />
          </Box>

          <Box
            sx={{
              alignItems: "flex-start",
              display: "flex",
              flexDirection: { lg: "row", xs: "column" },
              gap: { lg: 8, xs: 4 },
              justifyContent: "space-between",
              overflow: "visible",
              width: "100%"
            }}
          >
            <ProductPriceBlock onCheckout={goCheckout} />
            <ProductBulletsBlock />
          </Box>

          <Divider
            sx={{
              borderBottomWidth: "3px",
              borderColor: alpha(COLORS.PRIMARY_LIGHT, 0.2),
              my: { md: 2, xs: 1.5 }
            }}
          />

          <Box
            sx={{
              display: "grid",
              gap: { md: 4, xs: 4 },
              gridTemplateColumns: { lg: "repeat(3, 1fr)", xs: "1fr" },
              width: "100%",
              mb: 6
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h2"
                variant="body_strong_lg_black"
                sx={{ m: 0, mb: 2 }}
              >
                Precision Health Guidance
              </Typography>
              <Typography component="p" variant="body_dark_gray" sx={{ m: 0 }}>
                By looking at your DNA, it’s possible to uncover your personal
                risk of developing a serious illness—so your doctors can act
                early and help you stay healthy.
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h2"
                variant="body_strong_lg_black"
                sx={{ m: 0, mb: 2 }}
              >
                DNA Screenings
              </Typography>
              <Typography component="p" variant="body_dark_gray" sx={{ m: 0 }}>
                Our team can help you understand your results and take action.
                Plus, if you hit a roadblock with your healthcare, our team can
                help you get back on track. 
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h2"
                variant="body_strong_lg_black"
                sx={{ m: 0, mb: 2 }}
              >
                Cancer Navigation
              </Typography>
              <Typography component="p" variant="body_dark_gray" sx={{ m: 0 }}>
                If you are diagnosed with cancer, Genomic Life works alongside
                your doctors to help you fully understand treatment options and
                overcome barriers—so you can feel confident in your next steps. 
              </Typography>
            </Box>
          </Box>

          <InviteeProductFaqAccordions />

          <Box
            sx={{
              bgcolor: COLORS.BACKGROUND_BLUE,
              borderRadius: "24px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              gap: { lg: 2, xs: 1 },
              overflow: "visible",
              p: 3,
              width: "100%"
            }}
          >
            <Typography
              component="h4"
              variant="body_strong_xl_black"
              sx={{ m: 0, mb: -1 }}
            >
              Get started with your Genomic Life
            </Typography>
            <Typography component="p" variant="body_lg" sx={{ m: 0, mb: 4 }}>
              Take advantage of personalized insights and support to help you
              make informed decisions about your health.
            </Typography>
            <Box
              sx={{
                alignItems: "stretch",
                display: "flex",
                flexDirection: { lg: "row-reverse", xs: "column" },
                gap: { lg: 6, xs: 4 },
                overflow: "visible",
                width: "100%"
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flex: { lg: "0 1 50%", xs: "none" },
                  justifyContent: { lg: "flex-end", xs: "stretch" },
                  minWidth: 0,
                  width: { xs: "100%" }
                }}
              >
                <ProductPriceBlock
                  onCheckout={goCheckout}
                  sx={{
                    flex: { lg: "none", xs: "none" },
                    width: { lg: "auto", xs: "100%" }
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flex: { lg: "1 1 0", xs: "none" },
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  minHeight: 0,
                  minWidth: 0,
                  overflow: "visible"
                }}
              >
                <ProductBulletsBlock sx={{ flex: "none", pt: 0 }} />
              </Box>
            </Box>
          </Box>
        </Box>
        <Divider
          sx={{
            borderBottomWidth: "3px",
            borderColor: alpha(COLORS.PRIMARY_LIGHT, 0.2),
            my: { md: 2, xs: 1.5 }
          }}
        />
        <GenomicLifeLegalDisclaimer
          component="footer"
          policyLinks="public"
          sx={{ maxWidth: 880, pb: 2, pt: 1 }}
        />
      </Box>
    </GradientFramedLayout>
  );
};

export default GenomicLifeCompleteProductPage;
